import { useState, useRef } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PartnershipImageUploadProps {
  value: string;
  onChange: (url: string) => void;
}

export function PartnershipImageUpload({ value, onChange }: PartnershipImageUploadProps) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Errore",
        description: "Solo immagini sono supportate",
        variant: "error",
      });
      return;
    }

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Errore",
        description: "L'immagine deve essere inferiore a 5MB",
        variant: "error",
      });
      return;
    }

    setUploading(true);
    try {
      // Create canvas to crop to 5:4 aspect ratio
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          // Target aspect ratio 5:4 (horizontal)
          const targetRatio = 5 / 4;
          const imgRatio = img.width / img.height;

          let cropWidth = img.width;
          let cropHeight = img.height;
          let offsetX = 0;
          let offsetY = 0;

          if (imgRatio > targetRatio) {
            // Image is wider than 5:4, crop width
            cropWidth = img.height * targetRatio;
            offsetX = (img.width - cropWidth) / 2;
          } else {
            // Image is taller than 5:4, crop height
            cropHeight = img.width / targetRatio;
            offsetY = (img.height - cropHeight) / 2;
          }

          // Set canvas size (max 800x640 for output)
          const maxWidth = 800;
          const maxHeight = 640;
          canvas.width = Math.min(cropWidth, maxWidth);
          canvas.height = Math.min(cropHeight, maxHeight);

          ctx?.drawImage(
            img,
            offsetX,
            offsetY,
            cropWidth,
            cropHeight,
            0,
            0,
            canvas.width,
            canvas.height
          );
          resolve();
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });

      // Convert canvas to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Failed to create blob"))),
          "image/jpeg",
          0.85
        );
      });

      // Upload to Supabase storage
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.jpg`;
      const { data, error } = await supabase.storage
        .from("partnership-images")
        .upload(fileName, blob, { contentType: "image/jpeg" });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("partnership-images")
        .getPublicUrl(data.path);

      onChange(urlData.publicUrl);
      toast({ title: "Immagine caricata" });
    } catch (error: any) {
      toast({
        title: "Errore upload",
        description: error.message,
        variant: "error",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemove = () => {
    onChange("");
  };

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        className="hidden"
      />

      {value ? (
        <div className="relative border-[3px] border-dark rounded-none overflow-hidden">
          <img
            src={value}
            alt="Partnership preview"
            className="w-full aspect-[5/4] object-cover"
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1.5 bg-destructive text-destructive-foreground rounded-full border-2 border-dark shadow-[2px_2px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-[3px] border-dashed border-dark rounded-none p-8 text-center cursor-pointer hover:bg-muted/50 transition-colors aspect-[5/4] flex flex-col items-center justify-center"
        >
          {uploading ? (
            <div className="animate-pulse">
              <ImageIcon className="h-12 w-12 text-muted-foreground mb-2 mx-auto" />
              <p className="text-muted-foreground">Caricamento...</p>
            </div>
          ) : (
            <>
              <Upload className="h-12 w-12 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Clicca per caricare immagine (5:4)
              </p>
              <p className="text-xs text-muted-foreground mt-1">Max 5MB, JPG/PNG</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
